import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Download, BookOpen, Headphones, Users, Calendar, TrendingUp, RefreshCw, Search, X, Video } from 'lucide-react';
import { contentAnalyticsService, TopBookRow, TopAudioRow, TopUserRow, TopVideoRow } from '../services/contentAnalyticsService';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

type ReportType = 
  | 'topViewedBooks'
  | 'topReadingStartedBooks'
  | 'topCompletedBooks'
  | 'topSharedBooks'
  | 'topDownloadedBooks'
  | 'topViewedAudios'
  | 'topCompletedAudios'
  | 'topSharedAudios'
  | 'topDownloadedAudios'
  | 'topViewedVideos'
  | 'topSharedVideos'
  | 'topUsersEngagement';

const ContentAnalytics = () => {
  const [selectedReport, setSelectedReport] = useState<ReportType>('topViewedBooks');
  const [days, setDays] = useState<number>(30);
  const [daysInput, setDaysInput] = useState<string>('30');
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const daysTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const formatSecondsToHms = (seconds: number): string => {
    const s = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    
    const parts: string[] = [];
    if (h > 0) parts.push(`${h} Hr`);
    if (m > 0) parts.push(`${m} mins`);
    if (sec > 0 || parts.length === 0) parts.push(`${sec} secs`);
    
    return parts.join(', ');
  };

  const formatTimestamp = (timestamp: number | null | undefined): string => {
    if (!timestamp) return 'N/A';
    const date = new Date(Math.floor(timestamp / 1000)); // Convert microseconds to milliseconds
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isDownloadReport = (report: ReportType): boolean => {
    return report === 'topDownloadedBooks' || report === 'topDownloadedAudios';
  };

  const getCountLabel = (report: ReportType): string => {
    if (report === 'topDownloadedBooks' || report === 'topDownloadedAudios') {
      return 'Downloads';
    }
    return 'Count';
  };

  // Sync daysInput when days changes externally
  useEffect(() => {
    setDaysInput(days.toString());
  }, [days]);

  // Debounced days change handler
  useEffect(() => {
    if (daysTimeoutRef.current) {
      clearTimeout(daysTimeoutRef.current);
    }
    
    const numDays = parseInt(daysInput);
    if (!isNaN(numDays) && numDays >= 1 && numDays <= 90 && numDays !== days) {
      daysTimeoutRef.current = setTimeout(() => {
        setDays(numDays);
      }, 500);
    }

    return () => {
      if (daysTimeoutRef.current) {
        clearTimeout(daysTimeoutRef.current);
      }
    };
  }, [daysInput, days]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let result: any[] = [];

      switch (selectedReport) {
        case 'topViewedBooks':
          result = await contentAnalyticsService.getTopViewedBooks(days);
          console.log('Fetched books from API:', result.length);
          result = await contentAnalyticsService.enrichBookData(result);
          console.log('After enrichment:', result.filter(b => b.coverUrl).length, 'have cover images');
          break;
        case 'topReadingStartedBooks':
          result = await contentAnalyticsService.getTopReadingStartedBooks(days);
          result = await contentAnalyticsService.enrichBookData(result);
          break;
        case 'topCompletedBooks':
          result = await contentAnalyticsService.getTopCompletedBooks(days);
          result = await contentAnalyticsService.enrichBookData(result);
          break;
        case 'topSharedBooks':
          result = await contentAnalyticsService.getTopSharedBooks(days);
          result = await contentAnalyticsService.enrichBookData(result);
          break;
        case 'topDownloadedBooks':
          result = await contentAnalyticsService.getTopDownloadedBooks(days);
          result = await contentAnalyticsService.enrichBookData(result);
          break;
        case 'topViewedAudios':
          result = await contentAnalyticsService.getTopViewedAudios(days);
          result = await contentAnalyticsService.enrichAudioData(result);
          break;
        case 'topCompletedAudios':
          result = await contentAnalyticsService.getTopCompletedAudios(days);
          result = await contentAnalyticsService.enrichAudioData(result);
          break;
        case 'topSharedAudios':
          result = await contentAnalyticsService.getTopSharedAudios(days);
          result = await contentAnalyticsService.enrichAudioData(result);
          break;
        case 'topDownloadedAudios':
          result = await contentAnalyticsService.getTopDownloadedAudios(days);
          result = await contentAnalyticsService.enrichAudioData(result);
          break;
        case 'topViewedVideos':
          result = await contentAnalyticsService.getTopViewedVideos(days);
          result = await contentAnalyticsService.enrichVideoData(result);
          break;
        case 'topSharedVideos':
          result = await contentAnalyticsService.getTopSharedVideos(days);
          result = await contentAnalyticsService.enrichVideoData(result);
          break;
        case 'topUsersEngagement':
          result = await contentAnalyticsService.getTopUsersEngagement(days);
          result = await contentAnalyticsService.enrichUserData(result);
          break;
      }

      setData(result);
    } catch (error: any) {
      console.error('Error fetching analytics:', error);
      toast.error(error.message || 'Failed to fetch analytics data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [days, selectedReport]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportToExcel = () => {
    if (exportLoading || data.length === 0) return;

    try {
      setExportLoading(true);

      let excelData: any[] = [];
      const reportName = getReportName(selectedReport);

      if (selectedReport === 'topUsersEngagement') {
        // Export user engagement data
        excelData = (data as TopUserRow[]).map((row) => ({
          'Name': row.user_name || 'N/A',
          'Center': row.center || 'N/A',
          'Phone': row.user_id,
          'Engagement (Seconds)': Math.round(row.engagement_seconds * 100) / 100,
          'Sessions': row.sessions,
          'Book Views': row.book_views,
          'Books Completed': row.book_completed,
          'Audio Views': row.audio_views,
          'Audio Completed': row.audio_completed,
          'Last Active (IST)': row.last_active_datetime_ist,
        }));
      } else if (selectedReport.includes('Audio')) {
        // Export audio data (no thumbnails, just data)
        excelData = (data as TopAudioRow[]).map((row) => ({
          'Audio ID': row.audio_id,
          'Title': row.audio_title || 'N/A',
          'Author': row.audio_author || 'N/A',
          'Language': row.language || 'N/A',
          'Status': row.isAvailable !== false ? 'Available' : 'Deleted',
          'Event Count': row.event_count,
          'Description': row.description ? row.description.substring(0, 200) : '', // Truncate for Excel
        }));
      } else if (selectedReport.includes('Video')) {
        excelData = (data as TopVideoRow[]).map((row) => ({
          'Video ID': row.video_id,
          'Title': row.video_title || 'N/A',
          'Category': row.video_category || 'N/A',
          'Status': row.isAvailable !== false ? 'Available' : 'Deleted',
          'Event Count': row.event_count,
          'Downloads': row.download_count,
          'Unique Downloaders': row.unique_downloaders,
          'First Download': formatTimestamp(row.first_download_timestamp),
          'Last Download': formatTimestamp(row.last_download_timestamp),
          'Description': row.description ? row.description.substring(0, 200) : '',
        }));
      } else {
        // Export book data (no thumbnails, just data)
        excelData = (data as TopBookRow[]).map((row) => ({
          'Book ID': row.book_id,
          'Title': row.book_title || 'N/A',
          'Author': row.book_author || 'N/A',
          'Category': row.book_category || 'N/A',
          'Language': row.language || 'N/A',
          'Status': row.isAvailable !== false ? 'Available' : 'Deleted',
          'Event Count': row.event_count,
          'Description': row.description ? row.description.substring(0, 200) : '', // Truncate for Excel
        }));
      }

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, reportName);

      // Generate filename with current date
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${reportName.toLowerCase().replace(/\s+/g, '_')}_${days}days_${dateStr}.xlsx`;

      // Write file
      XLSX.writeFile(workbook, filename);

      toast.success(`Exported ${excelData.length} records to Excel`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    } finally {
      setExportLoading(false);
    }
  };

  const getReportName = (report: ReportType): string => {
    const names: Record<ReportType, string> = {
      topViewedBooks: 'Top Viewed Books',
      topReadingStartedBooks: 'Top Reading Started Books',
      topCompletedBooks: 'Top Completed Books',
      topSharedBooks: 'Top Shared Books',
      topDownloadedBooks: 'Top Downloaded Books',
      topViewedAudios: 'Top Viewed Audios',
      topCompletedAudios: 'Top Completed Audios',
      topSharedAudios: 'Top Shared Audios',
      topDownloadedAudios: 'Top Downloaded Audios',
      topViewedVideos: 'Top Viewed Videos',
      topSharedVideos: 'Top Shared Videos',
      topUsersEngagement: 'Top Users Engagement',
    };
    return names[report];
  };

  const getReportIcon = (report: ReportType) => {
    if (report.includes('Audio')) return Headphones;
    if (report.includes('Video')) return Video;
    if (report.includes('User')) return Users;
    return BookOpen;
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;

    const searchLower = searchTerm.toLowerCase();
    return data.filter((row) => {
      if (selectedReport === 'topUsersEngagement') {
        const userRow = row as TopUserRow;
        return (
          (userRow.user_name || '').toLowerCase().includes(searchLower) ||
          (userRow.center || '').toLowerCase().includes(searchLower) ||
          (userRow.user_id || '').toLowerCase().includes(searchLower) ||
          (userRow.email || '').toLowerCase().includes(searchLower)
        );
      } else if (selectedReport.includes('Audio')) {
        const audioRow = row as TopAudioRow;
        return (
          (audioRow.audio_title || '').toLowerCase().includes(searchLower) ||
          (audioRow.audio_author || '').toLowerCase().includes(searchLower) ||
          (audioRow.audio_id || '').toLowerCase().includes(searchLower) ||
          (audioRow.description || '').toLowerCase().includes(searchLower)
        );
      } else if (selectedReport.includes('Video')) {
        const videoRow = row as TopVideoRow;
        return (
          (videoRow.video_title || '').toLowerCase().includes(searchLower) ||
          (videoRow.video_category || '').toLowerCase().includes(searchLower) ||
          (videoRow.video_id || '').toLowerCase().includes(searchLower) ||
          (videoRow.description || '').toLowerCase().includes(searchLower)
        );
      } else {
        const bookRow = row as TopBookRow;
        return (
          (bookRow.book_title || '').toLowerCase().includes(searchLower) ||
          (bookRow.book_author || '').toLowerCase().includes(searchLower) ||
          (bookRow.book_id || '').toLowerCase().includes(searchLower) ||
          (bookRow.book_category || '').toLowerCase().includes(searchLower) ||
          (bookRow.description || '').toLowerCase().includes(searchLower)
        );
      }
    });
  }, [data, searchTerm, selectedReport]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Loading analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header - Compact */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 font-heading text-primary-800">Content Reports</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            View and export content engagement analytics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="btn btn-secondary flex items-center gap-1.5 text-sm px-3 py-1.5"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-600"></div>
                Loading...
              </>
            ) : (
              <>
                <RefreshCw className="w-3.5 h-3.5" />
                Refresh
              </>
            )}
          </button>
          <button
            onClick={exportToExcel}
            className="btn btn-primary flex items-center gap-1.5 text-sm px-3 py-1.5"
            disabled={data.length === 0 || exportLoading}
          >
            {exportLoading ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-3.5 h-3.5" />
                Export
              </>
            )}
          </button>
        </div>
      </div>

      {/* Report Selection and Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Report Type Selector */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Report Type
              </label>
              <select
                value={selectedReport}
                onChange={(e) => setSelectedReport(e.target.value as ReportType)}
                className="input w-full text-sm py-2"
              >
                <optgroup label="Books">
                  <option value="topViewedBooks">Top Viewed Books</option>
                  <option value="topReadingStartedBooks">Top Reading Started Books</option>
                  <option value="topCompletedBooks">Top Completed Books</option>
                  <option value="topSharedBooks">Top Shared Books</option>
                  <option value="topDownloadedBooks">Top Downloaded Books</option>
                </optgroup>
                <optgroup label="Audio">
                  <option value="topViewedAudios">Top Viewed Audios</option>
                  <option value="topCompletedAudios">Top Completed Audios</option>
                  <option value="topSharedAudios">Top Shared Audios</option>
                  <option value="topDownloadedAudios">Top Downloaded Audios</option>
                </optgroup>
                <optgroup label="Video">
                  <option value="topViewedVideos">Top Viewed Videos</option>
                  <option value="topSharedVideos">Top Shared Videos</option>
                </optgroup>
                <optgroup label="Users">
                  <option value="topUsersEngagement">Top Users Engagement</option>
                </optgroup>
              </select>
            </div>

            {/* Days Selector */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Days (1-90)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={daysInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 90)) {
                      setDaysInput(value);
                    }
                  }}
                  onBlur={() => {
                    const numDays = parseInt(daysInput);
                    if (isNaN(numDays) || numDays < 1 || numDays > 90) {
                      setDaysInput(days.toString());
                    }
                  }}
                  className="input w-full text-sm py-2"
                  placeholder="30"
                />
                <Calendar className="w-4 h-4 text-gray-400" />
              </div>
            </div>

            {/* Search */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Search
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search records..."
                  className="input w-full pl-10 pr-8 text-sm py-2"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Stats - Compact */}
          <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-3 text-xs text-gray-600">
            <div className="flex items-center gap-1.5">
              {React.createElement(getReportIcon(selectedReport), { className: "w-3.5 h-3.5" })}
              <span className="font-medium">{getReportName(selectedReport)}</span>
            </div>
            <span>•</span>
            <div className="flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>{filteredData.length} of {data.length} records</span>
            </div>
            {searchTerm && (
              <>
                <span>•</span>
                <span className="text-primary-600">Filtered</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            {data.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-500">No data available for the selected report and time period.</p>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-500">No records match your search criteria.</p>
              </div>
            ) : selectedReport === 'topUsersEngagement' ? (
              <table className="table text-sm">
                <thead>
                  <tr>
                    <th className="text-xs">User</th>
                    <th className="text-xs">Engagement</th>
                    <th className="text-xs">Sessions</th>
                    <th className="text-xs">Book Views</th>
                    <th className="text-xs">Books Completed</th>
                    <th className="text-xs">Audio Views</th>
                    <th className="text-xs">Audio Completed</th>
                    <th className="text-xs">Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  {(filteredData as TopUserRow[]).map((row, index) => (
                    <tr key={index}>
                      <td>
                        <div className="space-y-0.5">
                          <div className="font-medium text-sm text-gray-900">
                            {row.user_name || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-600">
                            {row.center || 'N/A'} • {row.user_id}
                          </div>
                        </div>
                      </td>
                      <td className="font-mono text-xs">{formatSecondsToHms(row.engagement_seconds)}</td>
                      <td className="text-sm">{row.sessions}</td>
                      <td className="text-sm">{row.book_views}</td>
                      <td className="text-sm">{row.book_completed}</td>
                      <td className="text-sm">{row.audio_views}</td>
                      <td className="text-sm">{row.audio_completed}</td>
                      <td className="text-xs">{row.last_active_datetime_ist}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : selectedReport.includes('Audio') ? (
              <table className="table text-sm">
                <thead>
                  <tr>
                    <th className="text-xs">Cover</th>
                    <th className="text-xs">Title</th>
                    <th className="text-xs">Author</th>
                    {isDownloadReport(selectedReport) && (
                      <>
                        <th className="text-xs">Downloads</th>
                        <th className="text-xs">Unique Downloaders</th>
                        <th className="text-xs">First Download</th>
                        <th className="text-xs">Last Download</th>
                      </>
                    )}
                    {!isDownloadReport(selectedReport) && (
                      <>
                        <th className="text-xs">Language</th>
                        <th className="text-xs">Status</th>
                      </>
                    )}
                    <th className="text-xs">{isDownloadReport(selectedReport) ? 'Metric' : 'Count'}</th>
                  </tr>
                </thead>
                <tbody>
                  {(filteredData as TopAudioRow[]).map((row, index) => (
                    <tr key={index}>
                      <td>
                        {row.coverUrl ? (
                          <img
                            src={row.coverUrl}
                            alt={row.audio_title || 'Audio cover'}
                            className="w-16 h-24 object-cover rounded shadow-sm"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/64x88?text=No+Image';
                            }}
                          />
                        ) : (
                          <div className="w-16 h-24 bg-gray-200 rounded shadow-sm flex items-center justify-center text-gray-400 text-[10px] text-center p-1">
                            No Image
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="font-medium text-sm">{row.audio_title || 'N/A'}</div>
                        {row.description && (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-1 max-w-xs">
                            {row.description}
                          </div>
                        )}
                        <div className="text-[10px] text-gray-400 font-mono mt-0.5">ID: {row.audio_id}</div>
                      </td>
                      <td className="text-sm">{row.audio_author || 'N/A'}</td>
                      {isDownloadReport(selectedReport) && (
                        <>
                          <td className="font-semibold text-sm">{row.download_count || 0}</td>
                          <td className="text-sm">{row.unique_downloaders || 0}</td>
                          <td className="text-xs text-gray-600">{formatTimestamp(row.first_download_timestamp)}</td>
                          <td className="text-xs text-gray-600">{formatTimestamp(row.last_download_timestamp)}</td>
                        </>
                      )}
                      {!isDownloadReport(selectedReport) && (
                        <>
                          <td className="text-sm">{row.language || 'N/A'}</td>
                          <td>
                            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                              row.isAvailable !== false
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {row.isAvailable !== false ? 'Available' : 'Deleted'}
                            </span>
                          </td>
                        </>
                      )}
                      <td className="font-semibold text-sm">{isDownloadReport(selectedReport) ? row.download_count || 0 : row.event_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : selectedReport.includes('Video') ? (
              <table className="table text-sm">
                <thead>
                  <tr>
                    <th className="text-xs">Thumbnail</th>
                    <th className="text-xs">Title</th>
                    <th className="text-xs">Category</th>
                    {isDownloadReport(selectedReport) && (
                      <>
                        <th className="text-xs">Downloads</th>
                        <th className="text-xs">Unique Downloaders</th>
                        <th className="text-xs">First Download</th>
                        <th className="text-xs">Last Download</th>
                      </>
                    )}
                    {!isDownloadReport(selectedReport) && (
                      <>
                        <th className="text-xs">Status</th>
                      </>
                    )}
                    <th className="text-xs">{isDownloadReport(selectedReport) ? 'Metric' : 'Count'}</th>
                  </tr>
                </thead>
                <tbody>
                  {(filteredData as TopVideoRow[]).map((row, index) => (
                    <tr key={index}>
                      <td>
                        {row.coverUrl ? (
                          <img
                            src={row.coverUrl}
                            alt={row.video_title || 'Video thumbnail'}
                            className="w-16 h-12 object-cover rounded shadow-sm"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/64x48?text=No+Image';
                            }}
                          />
                        ) : (
                          <div className="w-16 h-12 bg-gray-200 rounded shadow-sm flex items-center justify-center text-gray-400 text-[10px] text-center p-1">
                            No Image
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="font-medium text-sm">{row.video_title || 'N/A'}</div>
                        {row.description && (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-1 max-w-xs">
                            {row.description}
                          </div>
                        )}
                        <div className="text-[10px] text-gray-400 font-mono mt-0.5">ID: {row.video_id}</div>
                      </td>
                      <td className="text-sm">{row.video_category || 'N/A'}</td>
                      {isDownloadReport(selectedReport) && (
                        <>
                          <td className="font-semibold text-sm">{row.download_count || 0}</td>
                          <td className="text-sm">{row.unique_downloaders || 0}</td>
                          <td className="text-xs text-gray-600">{formatTimestamp(row.first_download_timestamp)}</td>
                          <td className="text-xs text-gray-600">{formatTimestamp(row.last_download_timestamp)}</td>
                        </>
                      )}
                      {!isDownloadReport(selectedReport) && (
                        <td>
                          <span
                            className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                              row.isAvailable !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {row.isAvailable !== false ? 'Available' : 'Deleted'}
                          </span>
                        </td>
                      )}
                      <td className="font-semibold text-sm">
                        {isDownloadReport(selectedReport) ? row.download_count || 0 : row.event_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="table text-sm">
                <thead>
                  <tr>
                    <th className="text-xs">Cover</th>
                    <th className="text-xs">Title</th>
                    <th className="text-xs">Author</th>
                    {isDownloadReport(selectedReport) && (
                      <>
                        <th className="text-xs">Downloads</th>
                        <th className="text-xs">Unique Downloaders</th>
                        <th className="text-xs">First Download</th>
                        <th className="text-xs">Last Download</th>
                      </>
                    )}
                    {!isDownloadReport(selectedReport) && (
                      <>
                        <th className="text-xs">Category</th>
                        <th className="text-xs">Language</th>
                        <th className="text-xs">Status</th>
                      </>
                    )}
                    <th className="text-xs">{isDownloadReport(selectedReport) ? 'Metric' : 'Count'}</th>
                  </tr>
                </thead>
                <tbody>
                  {(filteredData as TopBookRow[]).map((row, index) => (
                    <tr key={index}>
                      <td>
                        {row.coverUrl ? (
                          <img
                            src={row.coverUrl}
                            alt={row.book_title || 'Book cover'}
                            className="w-16 h-24 object-cover rounded shadow-sm"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/64x88?text=No+Image';
                            }}
                          />
                        ) : (
                          <div className="w-16 h-24 bg-gray-200 rounded shadow-sm flex items-center justify-center text-gray-400 text-[10px] text-center p-1">
                            No Image
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="font-medium text-sm">{row.book_title || 'N/A'}</div>
                        {row.description && (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-1 max-w-xs">
                            {row.description}
                          </div>
                        )}
                        <div className="text-[10px] text-gray-400 font-mono mt-0.5">ID: {row.book_id}</div>
                      </td>
                      <td className="text-sm">{row.book_author || 'N/A'}</td>
                      {isDownloadReport(selectedReport) && (
                        <>
                          <td className="font-semibold text-sm">{row.download_count || 0}</td>
                          <td className="text-sm">{row.unique_downloaders || 0}</td>
                          <td className="text-xs text-gray-600">{formatTimestamp(row.first_download_timestamp)}</td>
                          <td className="text-xs text-gray-600">{formatTimestamp(row.last_download_timestamp)}</td>
                        </>
                      )}
                      {!isDownloadReport(selectedReport) && (
                        <>
                          <td className="text-sm">{row.book_category || 'N/A'}</td>
                          <td className="text-sm">{row.language || 'N/A'}</td>
                          <td>
                            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                              row.isAvailable !== false
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {row.isAvailable !== false ? 'Available' : 'Deleted'}
                            </span>
                          </td>
                        </>
                      )}
                      <td className="font-semibold text-sm">{isDownloadReport(selectedReport) ? row.download_count || 0 : row.event_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Suggestions & Improvements */}
      {/* <div className="card bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <div className="card-body">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            Suggestions & Improvements
          </h3>
          <div className="space-y-2 text-sm text-gray-700">
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span><strong>Current Features:</strong> Export functionality, thumbnail display, enriched data from Firestore</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">→</span>
              <span><strong>Future Enhancements:</strong> Date range picker, comparison between periods, trend charts, filters by category/author</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-purple-600 font-bold">💡</span>
              <span><strong>Performance:</strong> Data is cached for 60 seconds. Use Refresh button to get latest data.</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-orange-600 font-bold">⚠</span>
              <span><strong>Note:</strong> Large datasets may take time to enrich. Consider pagination for reports with 100+ items.</span>
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default ContentAnalytics;

